/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { resetOrder, setOrder } from "../../../store/order";
import { setErrorMessage, startLoading, stopLoading } from "../../../store/ui";

import apiService from "../../../api/api.service";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { tracking_number } from "../../../config/config.js";
import { parseOrderByCharacter } from "../../../utils/helper";
import { replaceHref } from "../../../utils/replaceHref";
import { validateEmail, validateEmpty } from "../../../utils/validate";
import Spinner from "../../Spinner";

const TrackingFormTab = ({ order, shop }) => {
  const dispatch = useDispatch();
  const { tracking_form: i18n } = useSelector((state) => state.i18n);
  const {
    trackingForm: settings,
    mappingCourier,
    isEnabledEmail,
    isEnabledOrderId,
    isEnabledPhoneNumber,
    isEnabledTrackingNumber,
  } = useSelector((state) => state.settings);
  const [orderName, setOrderName] = useState("");
  const [email, setEmail] = useState("");
  const [trackingNumber, setTrackingNumber] = useState(tracking_number);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(window?.Shopify?.country);
  const [invalidMessage, setInvalidMessage] = useState({});
  const { isLoading } = useSelector((state) => state.ui);
  const validForm = () => {
    setInvalidMessage({});
    let isValid = true;
    if (settings.requireTrackingNumber && !validateEmpty(trackingNumber)) {
      setInvalidMessage((msg) => ({
        ...msg,
        trackingNumber: i18n.empty_tracking_number,
      }));
      isValid = false;
    }

    if (settings.requireOrderId && !validateEmpty(orderName)) {
      setInvalidMessage((msg) => ({
        ...msg,
        orderName: i18n.empty_order_name,
      }));
      isValid = false;
    }

    if (settings.requireEmail && !validateEmpty(email)) {
      setInvalidMessage((msg) => ({
        ...msg,
        email: i18n.empty_email,
      }));
      isValid = false;
    } else if (validateEmpty(email) && !validateEmail(email)) {
      setInvalidMessage((msg) => ({
        ...msg,
        email: i18n.invalid_email,
      }));
      isValid = false;
    }

    if (settings.requirePhoneNumber && !validateEmpty(phone)) {
      setInvalidMessage((msg) => ({
        ...msg,
        phoneNumber: i18n.empty_phone_number,
      }));
      isValid = false;
    }
    return isValid;
  };

  const handleTracking = async ({ event, type }) => {
    replaceHref(trackingNumber);
    event?.preventDefault();
    if (!validForm()) return;

    dispatch(startLoading());
    dispatch(setErrorMessage(""));
    dispatch(resetOrder());

    try {
      let endpoint;
      let req = { shop };

      switch (type) {
        case "email":
          endpoint = "submit_track_email";
          req.email = email;
          break;
        case "phone":
          endpoint = "submit_track_phone_number";
          req.phone_number = phone?.replace(/\s/g, "");
          break;
        case "tracking":
          endpoint = "submit_track_tracking_number";
          const trackingSubmit = trackingNumber
            ? trackingNumber?.includes("/")
              ? trackingNumber.replace("/", "")
              : trackingNumber
            : trackingNumber;
          req.tracking_number = trackingSubmit;
          break;
        default:
          endpoint = "submit_track_order_number";
          req.order = orderName;
          req.email = email;
      }

      const response = await apiService.post(endpoint, req);

      switch (response?.data?.state) {
        case 2:
          dispatch(setErrorMessage(i18n.order_not_found));
          return;
        case 1:
          const dataOrder = response?.data?.order;
          const dataMapping = (item) =>
            parseOrderByCharacter(mappingCourier, item);

          const newDataMapping = dataOrder.map((item) => {
            const { newTrackingDetail, newTrackingCompany } = dataMapping(item);
            return {
              ...item,
              tracking_detail: newTrackingDetail,
              tracking_company: newTrackingCompany,
            };
          });
          dispatch(setOrder(newDataMapping));
          return true;
        default:
          return;
      }
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleClickTracking = async (event) => {
    const trackingMethods = [
      {
        enabled: isEnabledTrackingNumber,
        value: trackingNumber,
        type: "tracking",
      },
      {
        enabled: isEnabledOrderId,
        value: orderName,
      },
      {
        enabled: isEnabledEmail,
        value: email,
        type: "email",
      },
      {
        enabled: isEnabledPhoneNumber,
        value: phone,
        type: "phone",
      },
    ];

    for (const method of trackingMethods) {
      if (method.enabled && method.value) {
        const result = await handleTracking({
          event,
          type: method.type,
        });
        if (result) return;
      }
    }
  };

  useEffect(() => {
    const fillTrackingNumberFromParams = async () => {
      let flag = false;
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        switch (key) {
          case "tracking_number":
            setTrackingNumber(value);
            handleTracking({ type: "tracking" });
            return;
          case "order_name":
            setOrderName(value);
            break;
          case "email":
            setEmail(value);
            break;
          default:
            return;
        }
      }
      if (flag) {
        handleTracking();
      }
    };

    fillTrackingNumberFromParams();
  }, []);
  useEffect(() => {
    setCountry(window?.Shopify?.country);
  }, [window]);

  const isDisabledTrackingButton = useMemo(() => {
    let isDisabled = true;
    const listFieldCheck = [
      {
        isEnabled: isEnabledTrackingNumber,
        value: trackingNumber,
      },
      {
        isEnabled: isEnabledOrderId,
        value: orderName,
      },
      {
        isEnabled: isEnabledEmail,
        value: email,
      },
      {
        isEnabled: isEnabledPhoneNumber,
        value: phone,
      },
    ];
    listFieldCheck.forEach((item) => {
      if (item.isEnabled && item.value) {
        isDisabled = false;
      }
    });
    return isDisabled;
  }, [
    isEnabledEmail,
    isEnabledOrderId,
    isEnabledPhoneNumber,
    isEnabledTrackingNumber,
    trackingNumber,
    orderName,
    email,
    phone,
  ]);

  const onKeyDown = useCallback(
    (event, type, tp) => {
      if (event.keyCode === 13) {
        if (isDisabledTrackingButton) return;
        if (type === "number") {
          if (tp === "phone") {
            handleTracking({ event, type: "phone" });
          } else {
            handleTracking({ event, type: "tracking" });
          }

          replaceHref(trackingNumber);
        } else if (type === "order") {
          handleTracking({ event });
        }
      }
    },
    [isDisabledTrackingButton, trackingNumber]
  );

  return (
    <div
      className={
        `omg-section omg-section--form omg-tracking-form-tab `
        // +
        //   (settings.showMethods === "order_name" ||
        //     settings.showMethods === "tracking_number") &&
        // "omg-tracking-form-one"
      }
    >
      {!order ? (
        <>
          <form id="form-track-order-name" className="omg-form">
            {i18n.order_description ? (
              <p className="form-text-color-white">{i18n.order_description}</p>
            ) : null}
            {isEnabledTrackingNumber ? (
              <div className="omg-form-group">
                <label
                  htmlFor="input-tracking-number"
                  className="omg-form__label"
                >
                  {i18n.tracking_number}
                </label>
                <input
                  type="text"
                  id="input-tracking-number"
                  className="omg-form__control omg-form__control--text"
                  placeholder={i18n.plh_tracking_number}
                  value={trackingNumber}
                  onChange={(event) => setTrackingNumber(event.target.value)}
                  onKeyDown={(event) => {
                    onKeyDown(event, "number");
                  }}
                />

                <span className="omg-form__invalid">
                  {invalidMessage.trackingNumber}
                </span>
              </div>
            ) : null}
            {isEnabledOrderId ? (
              <div className="omg-form-group">
                <label htmlFor="input-order-name" className="omg-form__label">
                  {i18n.order_id}
                </label>
                <input
                  type="text"
                  id="input-order-name"
                  className="omg-form__control omg-form__control--text"
                  placeholder={i18n.plh_order_id}
                  value={orderName}
                  onChange={(event) => setOrderName(event.target.value)}
                  onKeyDown={(event) => onKeyDown(event, "order")}
                />
                <span className="omg-form__invalid">
                  {invalidMessage.orderName}
                </span>
              </div>
            ) : null}
            {isEnabledEmail ? (
              <div className="omg-form-group">
                <label htmlFor="input-email" className="omg-form__label">
                  {i18n.email}
                </label>
                <input
                  type="text"
                  id="input-email"
                  className="omg-form__control omg-form__control--text"
                  placeholder={i18n.plh_email}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onKeyDown={(event) => onKeyDown(event, "order")}
                />
                <span className="omg-form__invalid">
                  {invalidMessage.email}
                </span>
              </div>
            ) : null}
            {isEnabledPhoneNumber ? (
              <div className="omg-form-group">
                <label htmlFor="input-phone" className="omg-form__label">
                  {i18n.phone}
                </label>
                <div className="advanced-react-phone">
                  <PhoneInput
                    id="input-phone"
                    country={country}
                    placeholder={i18n.plh_phone}
                    // enableSearch
                    onKeyDown={(event) => onKeyDown(event, "number", "phone")}
                    disableSearchIcon
                    value={phone}
                    onChange={(phone) => setPhone(phone)}
                  />
                </div>

                <span className="omg-form__invalid">
                  {invalidMessage.phoneNumber}
                </span>
              </div>
            ) : null}
            <button
              className="btn btn-warning btn-custom"
              type="button"
              disabled={isDisabledTrackingButton}
              onClick={handleClickTracking}
            >
              {isLoading ? <Spinner /> : i18n.button}
            </button>
          </form>
        </>
      ) : (
        ""
      )}
    </div>
  );
};

export default TrackingFormTab;
